import { easeOut, motion, useScroll, useTransform } from 'framer-motion'
// eslint-disable-next-line no-restricted-imports
import Image from 'next/image'
import { RefObject, useEffect, useRef, useState } from 'react'
import BookingWidget from '../BookingWidget/BookingWidget'
import Header from '../Header/Header'
import { HomepageHeroProps, Steps } from './HomepageHero'

interface HomepageHeroDesktopProps extends HomepageHeroProps {
  bannerHeight: number
  clientWidth: number
  clientHeight: number
  siteHeaderHeight: number
  sectionRef: RefObject<HTMLElement | null>
}

interface HomepageHeroDesktopStepProps extends Steps {
  index: number
}

function HomepageHeroDesktopStep({
  header,
  subheader,
  index,
}: HomepageHeroDesktopStepProps) {
  return (
    <motion.div
      className="flex-grow basis-0"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{
        opacity: 1,
        y: 0,
        transition: { delay: 2 + index / 3, duration: 1 },
      }}
      viewport={{ once: true, amount: 0.4 }}
    >
      <div className="inline-block type-body-7 px-[1.40625rem] py-[0.1875rem] bg-theme-accessibility rounded-[2.5rem] mb-8">
        STEP {index + 1}
      </div>
      <Header as="h3" overrideClassName className="type-headline-2 mb-2">
        {header}
      </Header>
      <p className="type-body-4">{subheader}</p>
    </motion.div>
  )
}

export default function HomepageHeroDesktop({
  header,
  subheader,
  image,
  imageAlt,
  stepsHeader,
  steps,
  bookingWidget,
  clientWidth,
  clientHeight,
  siteHeaderHeight,
  sectionRef,
  bannerHeight,
}: HomepageHeroDesktopProps) {
  const contentBRef = useRef<HTMLDivElement>(null)
  const [contentBHeight, setContentBHeight] = useState(0)
  const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false)
  const safeSteps = Array.isArray(steps)
    ? steps.filter((step): step is Steps => Boolean(step))
    : []
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['1 1', '0 0'],
  })

  const bgScale = useTransform(scrollYProgress, [1, 0], [1, 1.1])
  const bgBlur = useTransform(
    scrollYProgress,
    [1, 0.9, 0],
    ['blur(0px)', 'blur(0px)', 'blur(20px)'],
  )
  const bgWidth = useTransform(
    scrollYProgress,
    [1, 0.9],
    [clientWidth - 32 * 2, clientWidth],
  )
  const bgHeight = useTransform(
    scrollYProgress,
    [1, 0.9],
    [clientHeight - siteHeaderHeight - 120, clientHeight - siteHeaderHeight],
  )
  const bgRadius = useTransform(scrollYProgress, [1, 0.9], [16, 0])
  const stepHeaderOpacity = useTransform(scrollYProgress, [0.8, 0.7], [0, 1], {
    ease: easeOut,
  })
  const overlayBlack = useTransform(scrollYProgress, [1, 0], [0, 0.4])
  const overlayColor = useTransform(scrollYProgress, [1, 0], [0.3, 0.5])
  const overlayGradient = useTransform(
    [overlayBlack, overlayColor],
    ([b, c]) =>
      `linear-gradient(0deg, rgba(0,0,0,${b}) 0%, rgba(0,0,0,${b}) 100%), ` +
      `linear-gradient(0deg, rgba(28,41,56,${c}) 0%, rgba(28,41,56,${c}) 100%)`,
  )

  const heroImageAlt = imageAlt ?? 'Hero background image'

  useEffect(() => {
    const contentBElem = contentBRef.current
    if (!(contentBElem && clientWidth && clientHeight)) return
    setContentBHeight(contentBElem.offsetHeight)
  }, [clientHeight, clientWidth, contentBRef])

  useEffect(() => {
    setIsBackgroundLoaded(false)
  }, [image])

  const markImageReady = (): void => {
    setIsBackgroundLoaded(true)
  }

  return (
    <>
      <div className="absolute left-0 top-0 w-full h-full">
        <div
          className="sticky top-0 w-screen h-screen"
          style={{
            height: clientHeight,
            paddingTop: siteHeaderHeight + bannerHeight,
            marginTop: siteHeaderHeight * -1,
          }}
        >
          <div className="relative w-full h-full">
            <motion.div
              className="absolute left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%] overflow-hidden"
              style={{
                width: bgWidth,
                height: bgHeight,
                borderRadius: bgRadius,
              }}
            >
              <div
                className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(26,39,58,0.8),_rgba(10,16,28,1))] transition-opacity duration-500"
                style={{ opacity: isBackgroundLoaded ? 0 : 1 }}
                aria-hidden
              />
              <motion.div
                className="absolute left-1/2 top-1/2 w-screen h-screen"
                style={{
                  height: clientHeight - siteHeaderHeight,
                  scale: bgScale,
                  filter: bgBlur,
                  translateX: '-50%',
                  translateY: '-50%',
                  willChange: 'transform, filter',
                  backfaceVisibility: 'hidden',
                  backgroundImage: `url(${image})`,
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                }}
              >
                <Image
                  src={image}
                  alt={heroImageAlt}
                  fill
                  priority
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  sizes="100vw"
                  style={{
                    opacity: isBackgroundLoaded ? 1 : 0,
                    transition: 'opacity 500ms ease',
                  }}
                  className="absolute inset-0 h-full w-full object-cover"
                  onLoadingComplete={markImageReady}
                  onError={markImageReady}
                />
                <motion.div
                  aria-hidden
                  className="absolute inset-0"
                  style={{ backgroundImage: overlayGradient }}
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
      <div
        className="relative"
        style={{
          // The site header margin is incorrectly calculated. Subtract
          // the height of the header to align booking widget correctly
          marginTop: siteHeaderHeight - 76,
        }}
      >
        {bookingWidget && (
          <div className="px-16 pt-2">
            <BookingWidget {...bookingWidget} />
          </div>
        )}
        <div
          className="px-[9.5rem] mx-auto text-white flex flex-col"
          style={{
            height: clientHeight - siteHeaderHeight,
            marginTop: 80,
            paddingBottom: 30,
          }}
        >
          <div className="max-w-[43rem]">
            <Header
              as="h1"
              overrideClassName
              className="type-display-1 !font-normal [&_span]:!font-semibold mb-[2.375rem]"
            >
              {header}
            </Header>
            <p className="type-body-2">{subheader}</p>
          </div>
          {safeSteps.length > 0 && (
            <motion.div
              ref={contentBRef}
              style={{ opacity: stepHeaderOpacity }}
            >
              <h2 className="type-display-3 !font-normal [&_span]:!font-semibold my-20">
                {stepsHeader}
              </h2>
              <div
                className="flex gap-x-[5.25rem]"
                style={{
                  marginBottom:
                    (clientHeight - siteHeaderHeight) / 2 - contentBHeight / 2,
                }}
              >
                {safeSteps.map(({ header, subheader }, key) => (
                  <HomepageHeroDesktopStep
                    header={header}
                    subheader={subheader}
                    index={key}
                    key={key}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </>
  )
}
